import React, { forwardRef } from 'react';
import { styled, ButtonBase, CircularProgress } from '@mui/material';
import uuid from 'uuid';

import Dropdown from 'shell/components/Dropdown';
import Icon from 'shell/components/Icon';
import IconButton from 'shell/components/IconButton';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import { downloadFileFromURL, getAbsoluteURL, getS3FileURL } from 'utils/helper';

const SIZES = ['small', 'medium'];
const TYPES = ['file', 'link'];

const CustomButtonBase = styled(ButtonBase)(({ $disabled, theme }) => {
    return {
        border: `1px solid ${theme.palette.ensightful.neutral[100]}`,
        borderRadius: '8px',
        width: '100%',
        padding: theme.spacing(3),
        background: theme.palette.ensightful.neutral[50],
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
        },
        '&:hover': {
            borderColor: theme.palette.ensightful.neutral[200],
            color: theme.palette.ensightful.neutral[900],
        },
        '&:active': {
            borderColor: theme.palette.ensightful.neutral[300],
        },
        ...($disabled && {
            pointerEvents: 'none',
        }),
    };
});

const CustomIcon = styled(Icon)(({ size, theme }) => {
    return {
        alignSelf: 'center',
        fontSize: '1.5rem',
        color: theme.palette.ensightful.neutral[900],
        ...(size === 'medium' && {
            alignSelf: 'flex-start',
            fontSize: '2rem',
        }),
    };
});

const DeleteIcon = styled(Icon)(({ size, theme }) => {
    return {
        alignSelf: 'center',
        borderRadius: '4px',
        padding: size === 'small' ? theme.spacing(1.5) : theme.spacing(2),
        fontSize: '1.5rem',
        background: theme.palette.ensightful.neutral[500],
        color: theme.palette.ensightful.shades[0],
        ...(size === 'medium' && {
            alignSelf: 'flex-start',
            fontSize: '2rem',
        }),
    };
});

const CustomStack = styled(Stack)(() => {
    return {
        flex: 1,
        minWidth: 0,
    };
});

const CustomText = styled(Text)(({ type }) => {
    return {
        ...(type === 'link' && {
            textDecoration: 'underline',
        }),
    };
});

const CustomCircularProgress = styled(CircularProgress)(() => {
    return {
        width: '1.5rem !important',
        height: '1.5rem !important',
    };
});

const getIconKind = (type, resource) => {
    if (type === 'link') return 'link';
    let kind = '';
    switch (resource.data.type) {
        case 'application/pdf':
            kind = 'filePDF';
            break;
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            kind = 'fileWord';
            break;
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            kind = 'fileExcel';
            break;
        case 'application/vnd.ms-powerpoint':
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            kind = 'filePPT';
            break;
        case 'application/zip':
            kind = 'fileZIP';
            break;
        case 'image/gif':
        case 'image/jpeg':
        case 'image/png':
            kind = 'fileImg';
            break;
        default:
            kind = 'fileUnknown';
            break;
    }
    return kind;
};

const getResourceName = (type, resource) => {
    if (type === 'link') return resource.name || resource.url;
    return resource.data.name;
};

const getResourceType = (type, resource) => {
    if (type === 'link') return 'Link';
    let text = '';
    switch (resource.data.type) {
        case 'application/pdf':
            text = 'PDF';
            break;
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            text = 'DOC';
            break;
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            text = 'Excel';
            break;
        case 'application/vnd.ms-powerpoint':
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            text = 'Power Point';
            break;
        case 'application/zip':
            text = 'ZIP File';
            break;
        case 'image/gif':
        case 'image/jpeg':
        case 'image/png':
            text = 'Image';
            break;
        default:
            text = 'Unknown';
            break;
    }
    return text;
};

export default forwardRef(({ options, resource, size, type, onClick, onSelect, ...rest }, ref) => {
    if (!SIZES.includes(size)) throw new Error(`Shell: Invalid size "${size}" in component ResourceChip.`);
    if (!TYPES.includes(type)) throw new Error(`Shell: Invalid type "${type}" in component ResourceChip.`);

    const textVariant = {
        small: 'p3',
        medium: 'p2',
    }[size];

    if (type === 'link') {
        rest.href = getAbsoluteURL(resource.url);
        rest.rel = 'noopener noreferrer';
        rest.target = '_blank';
    }

    const isDisabled = !resource.active || resource.loading;

    return (
        <CustomButtonBase
            ref={ref}
            {...rest}
            aria-disabled={isDisabled}
            component={'div'}
            $disabled={isDisabled}
            role={'presentation'}
            onClick={(e) => {
                if (isDisabled) {
                    e.preventDefault();
                } else {
                    if (onClick) {
                        e.preventDefault();
                        onClick(e);
                    } else {
                        if (type === 'file') {
                            downloadFileFromURL(getS3FileURL(resource.data), resource.data.name);
                        }
                    }
                }
            }}
        >
            <Stack sx={{ width: '100%', minWidth: 0 }} alignItems={'center'} direction={'row'} spacing={3}>
                {resource.active ? (
                    <CustomIcon kind={getIconKind(type, resource)} size={size} />
                ) : (
                    <DeleteIcon kind={'delete'} size={size} />
                )}
                <CustomStack alignItems={'flex-start'}>
                    {resource.active ? (
                        <>
                            <CustomText noWrap type={type} variant={textVariant}>
                                {getResourceName(type, resource)}
                            </CustomText>
                            <Text color={'light'} variant={textVariant}>
                                {getResourceType(type, resource)}
                            </Text>
                        </>
                    ) : (
                        <Text variant={textVariant}>
                            {type === 'file' ? 'This file was deleted.' : 'This link was removed.'}
                        </Text>
                    )}
                </CustomStack>
                {resource.loading ? (
                    <CustomCircularProgress color={'primary'} />
                ) : (
                    resource.active &&
                    options?.length && (
                        <Dropdown
                            id={`dropdown-${uuid.v4()}`}
                            options={options}
                            rootComponent={
                                <IconButton
                                    aria-label={'more file options'}
                                    component={'div'}
                                    kind={'chevronDown'}
                                    size={'small'}
                                    variant={'square'}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                />
                            }
                            onSelect={(option) => onSelect(option, resource)}
                        />
                    )
                )}
            </Stack>
        </CustomButtonBase>
    );
});
